import Cleave from "cleave.js";
import $ from "jquery";

import config from "js/config";
import template from "js/templates/steps/confirm-phone.jst";
import { isConfirmPhoneTest, getUserID } from "js/views/app/util";
import { heapTrack } from "lib/analytics";
import { PHONE_STATE } from "lib/constants";
import { formatPhone, validatePhone } from "lib/util";
import wsStorage from "lib/wsStorage";
import util from "js/util";

import { BaseStep } from "./base";

function track(msg, data) {
    heapTrack(msg, {
        ...data,
        returning: !!window.completed,
    });
}

const ConfirmPhoneStep = BaseStep.extend({
    name: "STEP_CONFIRM_PHONE",

    template,

    ui: {
        changeContainer: ".confirm-phone-step__change-container",
        code: "input[name=code]",
        confirmButton: ".js-confirm-code",
        enterContainer: ".confirm-phone-step__enter-container",
        error: ".js-error",
        phone: "input[name=phone]",
        phoneError: ".js-phone-error",
        resend: ".js-resend",
        resendConfirm: ".js-resend-confirm",
        resendLink: ".js-resend a",
        sendButton: ".js-send-code",
        sendContainer: ".confirm-phone-step__send-container",
        updateButton: ".js-update-phone",
    },

    events: {
        "click @ui.sendButton": "onSendCode",
        "click .js-change": "onChange",
        "click .js-skip": "onSkip",
        "click @ui.confirmButton": "onConfirmCode",
        "click @ui.updateButton": "onUpdatePhone",
        "click @ui.resendLink": "onResendCode",
    },

    shouldSkip() {
        return (
            !isConfirmPhoneTest() ||
            window.phoneState === PHONE_STATE.VERIFIED ||
            !!wsStorage.getItem("confirm-phone-skip")
        );
    },

    postInit() {
        this.showNextButton = false;
    },

    serializeData() {
        return {
            // Apparently we don't store this on the user model!!
            phone: formatPhone(window.user.phone),
        };
    },

    onSendCode(event) {
        event.preventDefault();

        this.sending = true;
        this.ui.sendButton.loading(true);

        $.ajax({
            type: "POST",
            url: "/api/v1/profile/phone/send/",
        })
            .done(() => {
                track("Phone Confirm - Send Code");

                this.showContainer(this.ui.sendContainer, this.ui.enterContainer);
            })
            .fail(({ status }) => {
                track("Phone Confirm - Send Code Fail", { status });
                util.showToast("We were unable to send a code. Please try again.", 4000);
            })
            .always(() => {
                this.sending = false;
                this.ui.sendButton.loading(false);
            });
    },

    onResendCode(event) {
        this.onSendCode(event);

        this.showContainer(this.ui.resend, this.ui.resendConfirm);
    },

    onChange(event) {
        event.preventDefault();

        if (this.sending) {
            return;
        }

        this.showContainer(this.ui.sendContainer, this.ui.changeContainer);
        /* eslint-disable no-new */
        new Cleave(this.ui.phone, config.cleave.PHONE_CONFIG);
        /* eslint-enable */
    },

    onSkip(event) {
        event.preventDefault();

        if (this.sending) {
            return;
        }

        // Don't show this again for a day?
        wsStorage.setItem("confirm-phone-skip", 1, 86400 * 1000);
        this.nextStep();
    },

    onConfirmCode(event) {
        event.preventDefault();

        this.ui.error.hide();

        const code = this.ui.code.val().replace(/\D/g, "").trim();
        if (code.length !== 6) {
            this.showError("Please enter a valid 6-digit code");
            return;
        }

        this.sending = true;
        this.ui.confirmButton.loading(true);

        $.ajax({
            type: "POST",
            url: "/api/v1/profile/phone/confirm/",
            data: JSON.stringify({
                code: this.ui.code.val(),
            }),
            dataType: "json",
            contentType: "application/json",
        })
            .done(() => {
                window.phoneState = PHONE_STATE.VERIFIED;
                track("Phone Confirm - Success", {
                    uid: getUserID(),
                });

                this.nextStep();
            })
            .fail(({ status }) => {
                track("Phone Confirm - Fail", {
                    status,
                });

                if (status === 404) {
                    this.showError("The code has expired. Click resend below to get a new code.");
                } else if (status === 403) {
                    this.showError("The code you entered does not match. Please try again.");
                } else {
                    this.showError("An error occurred. Please try again.");
                }
            })
            .always(() => {
                this.sending = false;
                this.ui.confirmButton.loading(false);
            });
    },

    onUpdatePhone(event) {
        event.preventDefault();

        this.ui.phoneError.hide();

        const phone = this.ui.phone.val().replace(/\D/g, "").trim();
        if (!validatePhone(phone)) {
            this.ui.phoneError.text("Please enter a valid phone number").show();
            return;
        }

        this.sending = true;
        this.ui.updateButton.loading(true);

        $.ajax({
            type: "PATCH",
            url: "/api/v1/auth",
            data: JSON.stringify({
                csrf_token: window.csrfToken,
                phone,
            }),
            dataType: "json",
            contentType: "application/json",
        })
            .done(() => {
                window.user.phone = phone;

                // Re-render so the new number shows up properly
                this.render();
            })
            .fail(() => {
                this.ui.phoneError.text("We could not update your number. Please try again").show();
            })
            .always(() => {
                this.sending = false;
                this.ui.updateButton.loading(false);
            });
    },

    showContainer($from, $to) {
        $from.fadeOut("fast", () => {
            $to.fadeIn("fast");
        });
    },

    showError(msg) {
        this.ui.error.text(msg).show();
    },

    onRender() {
        this.getNextButton().fadeOut();

        if (!this.rendered) {
            track("Phone Confirm - Shown");
            this.rendered = true;
        }
    },
});

export default ConfirmPhoneStep;
